/**
 * useIdleDetector — Squad G, Sprint Apr-30 2026
 *
 * Listens to mouse/keyboard/focus/blur activity on the window.
 * When the user has been idle for `thresholdMinutes`, posts an idle-segment
 * to the backend. When activity resumes, closes that segment.
 *
 * Also posts a keepalive ping every 60 seconds to prevent the server-side
 * reaper from closing the session prematurely.
 *
 * On window unload, sends a synchronous logout ping (navigator.sendBeacon).
 *
 * Design constraints:
 *   - Does NOT fire a request on every mousemove — uses a debounce timer.
 *   - Threshold is sourced from `activityInactivityMinutes` via prop (default 3).
 *   - All requests are best-effort (errors silently swallowed).
 */

import { useEffect, useRef, useCallback } from 'react';
import { api } from '../utils/api';

interface UseIdleDetectorOptions {
  /** Minutes of inactivity before posting an idle-segment. Default: 3. */
  thresholdMinutes?: number;
  /** Whether idle detection is active. Set false when user is not logged in. */
  enabled?: boolean;
}

const KEEPALIVE_INTERVAL_MS = 60_000; // 1 minute
const DEFAULT_THRESHOLD_MINUTES = 3;

export function useIdleDetector({
  thresholdMinutes = DEFAULT_THRESHOLD_MINUTES,
  enabled = true,
}: UseIdleDetectorOptions = {}) {
  const thresholdMs = thresholdMinutes * 60_000;

  // Refs (stable across renders, no re-subscriptions needed)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keepaliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isIdleRef = useRef(false);
  const idleStartRef = useRef<string | null>(null);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const postIdleStart = useCallback(async () => {
    const startedAt = new Date().toISOString();
    idleStartRef.current = startedAt;
    try {
      await api.post('/employee-attendance/idle-segment', {
        startedAt,
        reason: 'idle',
      });
    } catch {
      // Best-effort — don't surface errors to UI
    }
  }, []);

  const postIdleEnd = useCallback(async () => {
    idleStartRef.current = null;
    try {
      await api.post('/employee-attendance/idle-segment/close', {});
    } catch {
      // Best-effort
    }
  }, []);

  const sendKeepalive = useCallback(async () => {
    try {
      await api.post('/employee-attendance/keepalive', {});
    } catch {
      // Best-effort
    }
  }, []);

  // ── Activity handler ─────────────────────────────────────────────────────────

  const handleActivity = useCallback(() => {
    if (!enabled) return;

    // If we were idle, signal resume
    if (isIdleRef.current) {
      isIdleRef.current = false;
      postIdleEnd();
    }

    // Reset the idle countdown
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      isIdleRef.current = true;
      postIdleStart();
    }, thresholdMs);
  }, [enabled, thresholdMs, postIdleStart, postIdleEnd]);

  // ── Mount / unmount ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!enabled) return;

    // Start the idle countdown immediately on mount
    handleActivity();

    // Activity events — use { passive: true } for scroll/mousemove perf
    const events: Array<[string, boolean]> = [
      ['mousemove', true],
      ['mousedown', false],
      ['keydown', false],
      ['touchstart', true],
      ['focus', false],
      ['scroll', true],
    ];
    for (const [evt, passive] of events) {
      window.addEventListener(evt, handleActivity, { passive });
    }

    // Visibility change — treat losing focus as activity pause
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // User switched tabs / minimised — start idle early
        if (!isIdleRef.current) {
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
          isIdleRef.current = true;
          postIdleStart();
        }
      } else {
        // Tab back in — resume
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Keepalive ping every 60 s
    keepaliveTimerRef.current = setInterval(sendKeepalive, KEEPALIVE_INTERVAL_MS);

    // Logout on unload (sendBeacon for reliability)
    const handleUnload = () => {
      try {
        // Post attendance logout via beacon (fire-and-forget)
        const baseUrl =
          (window as any).__VITE_API_BASE__ ||
          (import.meta as any).env?.VITE_API_URL ||
          '/api/v1';
        navigator.sendBeacon(`${baseUrl}/employee-attendance/logout`);
      } catch {
        // Ignore
      }
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      // Cleanup
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (keepaliveTimerRef.current) clearInterval(keepaliveTimerRef.current);
      for (const [evt] of events) {
        window.removeEventListener(evt, handleActivity);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [enabled, handleActivity, postIdleStart, sendKeepalive]);

  // ── Expose a manual logout helper for the logout button ─────────────────────
  const reportLogout = useCallback(async () => {
    try {
      await api.post('/employee-attendance/logout', {});
    } catch {
      // Best-effort
    }
  }, []);

  return { reportLogout };
}
